<template>
  <div class="search-form">
    <h1 class="search-page__headline headline-large">
      {{ $t('search.title') }}
    </h1>
    <div class="search-form__wrapper">
      <div class="search-form__inner">
        <SearchOrigin />
        <SearchDestination />
        <DirectionButton />
      </div>
      <div class="search-form__calendar-desktop">
        <TravelDate />
        <ReturnDate />
      </div>
      <div class="search-form__calendar-mobile">
        <TravelDate />
        <ReturnDate />
      </div>
      <button
        class="search-form__button"
        :class="{ disabled: !canProceed }"
        :disabled="!canProceed"
        @click="handleSubmit">
        {{ $t('search.continue') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import { useUserStore } from '@/stores/userStore'
  import { getToken } from '@/services/tokenService'
  import SearchOrigin from '@/components/TravelPlanner/SearchOrigin/SearchOrigin.vue'
  import SearchDestination from '@/components/TravelPlanner/SearchDestination/SearchDestination.vue'
  import TravelDate from '@/components/TravelPlanner/Traveldate/TravelDate.vue'
  import ReturnDate from '@/components/TravelPlanner/ReturnDate/ReturnDate.vue'
  import DirectionButton from '@/components/TravelPlanner/DirectionButton.vue'

  // Store setup
  const journeyStore = useJourneyStore()
  const bookingStore = useBookingStore()
  const myTravelStore = useMyTravelStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()

  const { OriginStation, destinationStation, departureDate, returnDate } = storeToRefs(journeyStore)

  const canProceed = computed(() => {
    return !!OriginStation.value?.name && !!destinationStation.value?.name && !!departureDate.value
  })

  const handleSubmit = () => {
    const query: Record<string, string> = {
      origin: OriginStation.value?._u_i_c_station_code || OriginStation.value?.name || '',
      destination:
        destinationStation.value?._u_i_c_station_code || destinationStation.value?.name || '',
      outboundDate: departureDate.value || '',
      fromLight: 'true',
    }

    if (returnDate.value) {
      query.returnDate = returnDate.value
    }

    // Redirect to SPA
    const queryString = new URLSearchParams(query).toString()
    const spaBaseUrl = import.meta.env.VITE_SPA_BASE_URL
    window.location.href = `${spaBaseUrl}/?${queryString}`
  }

  onMounted(async () => {
    try {
      // Verify and restore token if user is logged in
      if (
        userStore.isLoggedIn &&
        (!authStore.token ||
          !authStore.tokenExpirationTime ||
          Date.now() >= authStore.tokenExpirationTime)
      ) {
        try {
          await getToken(false)
        } catch (error) {
          console.error('Failed to restore token:', error)
        }
      }

      // Reset stores
      bookingStore.$reset()
      myTravelStore.reset()
      journeyStore.clearDiscountCards()
      bookingStore.setIsRebooking(false)

      // Clear specific data
      if (journeyStore.selectedSeats.length > 0) {
        journeyStore.selectedSeats = []
      }
      if (journeyStore.selectedProducts.length > 0) {
        journeyStore.selectedProducts = []
      }
      if (bookingStore.selectedEquipment.outbound.length > 0) {
        bookingStore.selectedEquipment.outbound = []
      }
      if (bookingStore.selectedEquipment.inbound.length > 0) {
        bookingStore.selectedEquipment.inbound = []
      }
    } catch (error) {
      console.error('Error initializing SearchForm:', error)
    }
  })
</script>

<style scoped lang="scss">
  .search-form {
    width: 100%;
    max-width: max-content;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .search-page__headline {
    margin-block: 0.67em;
  }

  .search-form__wrapper {
    position: relative;
    width: min-content;
    max-width: max-content;
    margin: 0 auto;
    background-color: $clr-white;
    padding: 24px;
    color: #333;
    box-shadow: $box-shadow-card;
    border-radius: $border-radius;
    border: 1px solid lightgray;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    box-sizing: border-box;

    @media (min-width: $breakpoint-sm) {
      max-width: 400px;
    }

    @media (min-width: $breakpoint-xl) {
      flex-direction: row;
      align-items: center;
      max-width: max-content;
    }

    & > * {
      flex: 1 1 auto;
    }

    .search-form__inner {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      @media (min-width: $breakpoint-xl) {
        flex-direction: row;
      }

      & > * {
        flex: 1 1 auto;
      }
    }

    .search-form__calendar-mobile {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      flex: 1 1 auto;
      gap: 12px;

      & > * {
        flex: 1;
      }

      @media (min-width: $breakpoint-xl) {
        display: none;
      }
    }

    .search-form__calendar-desktop {
      display: none;

      @media (min-width: $breakpoint-xl) {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        gap: 16px;

        & > * {
          flex: 1;
        }
      }
    }

    .search-form__button {
      padding: rem(20) 40px;
      background-color: $clr-primary;
      color: $clr-white;
      border: none;
      border-radius: $border-radius;
      cursor: pointer;
      font-size: rem(16);
      line-height: rem(24);
      text-align: center;

      &.disabled {
        @include button-disabled;
      }
    }
  }
</style>
